import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from "@/components/ui/dialog";
import { 
  Palette, 
  Megaphone, 
  Video, 
  Globe, 
  Target, 
  Layers, 
  BarChart3, 
  Package,
  ArrowRight,
  Check,
  Quote,
  Menu,
  X,
  Linkedin,
  Twitter,
  Instagram,
  Mail,
  ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== COMPONENTS ====================

// Navigation
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-brand-bg/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="container-custom flex items-center justify-between py-4">
        <a href="/" className="flex items-center gap-2" data-testid="logo-link">
          <span className="font-heading font-bold text-2xl text-white">Brand</span>
          <span className="font-heading font-bold text-2xl text-brand-primary">Up+</span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => scrollToSection("services")} className="text-white/70 hover:text-white transition-colors font-medium" data-testid="nav-services">
            Services
          </button>
          <button onClick={() => scrollToSection("work")} className="text-white/70 hover:text-white transition-colors font-medium" data-testid="nav-work">
            Work
          </button>
          <button onClick={() => scrollToSection("process")} className="text-white/70 hover:text-white transition-colors font-medium" data-testid="nav-process">
            Process
          </button>
          <button onClick={() => scrollToSection("pricing")} className="text-white/70 hover:text-white transition-colors font-medium" data-testid="nav-pricing">
            Pricing
          </button>
          <button 
            onClick={() => scrollToSection("contact")} 
            className="btn-primary"
            data-testid="nav-contact-btn"
          >
            Get Started
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
          aria-label="Toggle mobile menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-brand-bg/98 backdrop-blur-md border-t border-white/10" data-testid="mobile-menu">
          <div className="container-custom py-6 flex flex-col gap-4">
            <button onClick={() => scrollToSection("services")} className="text-white/70 hover:text-white transition-colors font-medium text-left py-2" data-testid="mobile-nav-services">
              Services
            </button>
            <button onClick={() => scrollToSection("work")} className="text-white/70 hover:text-white transition-colors font-medium text-left py-2" data-testid="mobile-nav-work">
              Work
            </button>
            <button onClick={() => scrollToSection("process")} className="text-white/70 hover:text-white transition-colors font-medium text-left py-2" data-testid="mobile-nav-process">
              Process
            </button>
            <button onClick={() => scrollToSection("pricing")} className="text-white/70 hover:text-white transition-colors font-medium text-left py-2" data-testid="mobile-nav-pricing">
              Pricing
            </button>
            <button 
              onClick={() => scrollToSection("contact")} 
              className="btn-primary mt-4 text-center"
              data-testid="mobile-nav-contact-btn"
            >
              Get Started
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const clientLogos = [
    { name: "TechCorp", id: 1 },
    { name: "StartupX", id: 2 },
    { name: "GrowthCo", id: 3 },
    { name: "BrandLab", id: 4 },
    { name: "ScaleUp", id: 5 },
    { name: "VentureOne", id: 6 },
  ];

  return (
    <section className="min-h-screen hero-gradient relative pt-24 md:pt-32" data-testid="hero-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[80vh]">
          {/* Left Content */}
          <div className="animate-fade-in-up">
            <span className="inline-block text-brand-primary uppercase tracking-widest text-sm font-semibold mb-6">
              Creative Agency
            </span>
            <h1 className="hero-headline font-heading font-black text-white mb-6">
              BrandUp+ — Turn attention into{" "}
              <span className="text-brand-primary">growth.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 mb-8 max-w-xl leading-relaxed">
              We design brands, craft stories and produce content that moves people — and revenue.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button 
                onClick={() => scrollToSection("contact")}
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="hero-cta-primary"
              >
                Start Your Growth
                <ArrowRight size={18} />
              </button>
              <button 
                onClick={() => scrollToSection("work")}
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="hero-cta-secondary"
              >
                See Our Work
              </button>
            </div>

            {/* Client Logos */}
            <div className="border-t border-white/10 pt-8">
              <p className="text-white/50 text-sm uppercase tracking-widest mb-4">Trusted by</p>
              <div className="flex flex-wrap gap-8 items-center">
                {clientLogos.map((logo) => (
                  <div 
                    key={logo.id} 
                    className="text-white/30 font-heading font-bold text-lg hover:text-white/50 transition-colors"
                    data-testid={`client-logo-${logo.id}`}
                  >
                    {logo.name}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative animate-fade-in delay-300 hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-primary/20 to-brand-accent/20 blur-3xl opacity-50"></div>
              <img 
                src="https://images.unsplash.com/photo-1758873268877-3cd8ed329ed8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Creative agency workspace"
                className="relative w-full h-[500px] object-cover"
                loading="lazy"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Palette,
      title: "Branding & Identity",
      description: "Strategic brand foundations that differentiate and resonate.",
      metric: "+180% brand recall improvement",
    },
    {
      icon: Megaphone,
      title: "Digital Marketing & Social",
      description: "Data-driven campaigns that convert across channels.",
      metric: "3x average ROAS increase",
    },
    {
      icon: Video,
      title: "Audiovisual Production",
      description: "Compelling video content that captivates and converts.",
      metric: "85% higher engagement rates",
    },
    {
      icon: Globe,
      title: "Web & Landing Pages",
      description: "High-converting digital experiences that drive action.",
      metric: "+65% conversion rate lift",
    },
  ];

  return (
    <section id="services" className="py-24 md:py-32" data-testid="services-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="inline-block text-brand-secondary uppercase tracking-widest text-sm font-semibold mb-4">
            What We Do
          </span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
            Services Built for Growth
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            End-to-end creative and marketing solutions designed to scale your brand.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <div 
              key={service.title}
              className="service-card p-8 animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`service-card-${index}`}
            >
              <div className="icon-box mb-6">
                <service.icon size={24} className="text-brand-primary" />
              </div>
              <h3 className="font-heading font-bold text-xl text-white mb-3">
                {service.title}
              </h3>
              <p className="text-white/60 mb-4">
                {service.description}
              </p>
              <p className="text-brand-primary font-semibold text-sm">
                {service.metric}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why BrandUp Section
const WhySection = () => {
  const reasons = [
    {
      icon: Target,
      title: "Strategy-First",
      description: "Every project starts with deep research and strategic planning, not templates.",
    },
    {
      icon: Layers,
      title: "Cross-Channel Execution",
      description: "Seamless integration across all touchpoints for cohesive brand experiences.",
    },
    {
      icon: BarChart3,
      title: "Measurable KPIs",
      description: "Clear metrics and reporting so you always know what's working.",
    },
    {
      icon: Package,
      title: "End-to-End Delivery",
      description: "From concept to launch, we handle everything under one roof.",
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-white/[0.02]" data-testid="why-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block text-brand-accent uppercase tracking-widest text-sm font-semibold mb-4">
              Why BrandUp+
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Built Different. <br />
              <span className="text-brand-primary">Results Proven.</span>
            </h2>
            <p className="text-white/60 text-lg mb-8">
              We're not just another agency. We're growth partners who obsess over your success.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {reasons.map((reason, index) => (
              <div 
                key={reason.title}
                className="p-6 border border-white/10 hover:border-brand-primary/30 transition-colors"
                data-testid={`why-card-${index}`}
              >
                <reason.icon size={32} className="text-brand-primary mb-4" />
                <h3 className="font-heading font-bold text-lg text-white mb-2">
                  {reason.title}
                </h3>
                <p className="text-white/60 text-sm">
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    { number: "01", title: "Discover", description: "Deep-dive into your brand, audience, and goals." },
    { number: "02", title: "Strategy", description: "Craft a roadmap tailored to your growth objectives." },
    { number: "03", title: "Create", description: "Design and produce assets that stand out." },
    { number: "04", title: "Launch", description: "Deploy campaigns across optimal channels." },
    { number: "05", title: "Scale", description: "Optimize, iterate, and amplify what works." },
  ];

  return (
    <section id="process" className="py-24 md:py-32" data-testid="process-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="inline-block text-brand-primary uppercase tracking-widest text-sm font-semibold mb-4">
            Our Process
          </span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white">
            From Vision to Victory
          </h2>
        </div>

        <div className="relative">
          {/* Desktop Timeline */}
          <div className="hidden md:block">
            <div className="flex justify-between items-start relative">
              {/* Connecting Line */}
              <div className="absolute top-8 left-0 right-0 h-[2px] bg-gradient-to-r from-brand-primary/50 via-brand-secondary/50 to-brand-accent/50"></div>
              
              {steps.map((step, index) => (
                <div 
                  key={step.number}
                  className="relative flex flex-col items-center text-center w-1/5 animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`process-step-${index}`}
                >
                  <div className="w-16 h-16 bg-brand-bg border-2 border-brand-primary flex items-center justify-center mb-4 relative z-10">
                    <span className="font-heading font-bold text-brand-primary">{step.number}</span>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white mb-2">{step.title}</h3>
                  <p className="text-white/60 text-sm px-2">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="md:hidden space-y-8">
            {steps.map((step, index) => (
              <div 
                key={step.number}
                className="flex gap-6 items-start"
                data-testid={`process-step-mobile-${index}`}
              >
                <div className="w-14 h-14 flex-shrink-0 bg-brand-bg border-2 border-brand-primary flex items-center justify-center">
                  <span className="font-heading font-bold text-brand-primary text-sm">{step.number}</span>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-white mb-1">{step.title}</h3>
                  <p className="text-white/60 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Case Studies Section
const CaseStudiesSection = () => {
  const [selectedCase, setSelectedCase] = useState(null);

  const caseStudies = [
    {
      id: 1,
      client: "TechVenture",
      image: "https://images.unsplash.com/photo-1763191213523-1489179a1088?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      problem: "Low brand awareness in a crowded SaaS market",
      solution: "Complete rebrand + integrated digital campaign",
      result: "+45% website traffic, 2x demo requests",
      category: "Branding & Marketing",
    },
    {
      id: 2,
      client: "GrowthRetail",
      image: "https://images.unsplash.com/photo-1758518732175-5d608ba3abdf?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      problem: "E-commerce conversion rate below industry average",
      solution: "Landing page optimization + video content",
      result: "x3 sales in 90 days",
      category: "Web & Video",
    },
    {
      id: 3,
      client: "FinanceFlow",
      image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?w=600",
      problem: "Struggling to reach millennial investors",
      solution: "Social media strategy + influencer partnerships",
      result: "+120% social engagement, 50K new followers",
      category: "Social & Content",
    },
  ];

  return (
    <section id="work" className="py-24 md:py-32 bg-white/[0.02]" data-testid="case-studies-section">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="inline-block text-brand-secondary uppercase tracking-widest text-sm font-semibold mb-4">
              Featured Work
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white">
              Results That Speak
            </h2>
          </div>
          <p className="text-white/60 max-w-md">
            Real transformations for real brands. See how we've helped companies like yours grow.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {caseStudies.map((caseStudy, index) => (
            <div 
              key={caseStudy.id}
              className="case-study-card group cursor-pointer animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
              onClick={() => setSelectedCase(caseStudy)}
              data-testid={`case-study-card-${index}`}
            >
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={caseStudy.image}
                  alt={caseStudy.client}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/50 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 case-study-content">
                  <span className="text-brand-primary text-sm font-semibold uppercase tracking-wider">
                    {caseStudy.category}
                  </span>
                  <h3 className="font-heading font-bold text-2xl text-white mt-2 mb-3">
                    {caseStudy.client}
                  </h3>
                  <p className="text-brand-primary font-bold">{caseStudy.result}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Case Study Modal */}
        <Dialog open={!!selectedCase} onOpenChange={() => setSelectedCase(null)}>
          <DialogContent className="bg-brand-bg border border-white/10 max-w-2xl" data-testid="case-study-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-white">
                {selectedCase?.client}
              </DialogTitle>
              <DialogDescription className="text-brand-primary">
                {selectedCase?.category}
              </DialogDescription>
            </DialogHeader>
            {selectedCase && (
              <div className="space-y-6 mt-4">
                <img 
                  src={selectedCase.image}
                  alt={selectedCase.client}
                  className="w-full h-48 object-cover"
                />
                <div>
                  <h4 className="text-white/50 uppercase text-sm tracking-wider mb-2">The Challenge</h4>
                  <p className="text-white">{selectedCase.problem}</p>
                </div>
                <div>
                  <h4 className="text-white/50 uppercase text-sm tracking-wider mb-2">Our Solution</h4>
                  <p className="text-white">{selectedCase.solution}</p>
                </div>
                <div className="bg-brand-primary/10 border border-brand-primary/30 p-4">
                  <h4 className="text-brand-primary uppercase text-sm tracking-wider mb-2">Results</h4>
                  <p className="text-white font-bold text-xl">{selectedCase.result}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      id: 1,
      quote: "BrandUp+ transformed our entire digital presence. The ROI on their work paid for itself in the first month.",
      name: "Sarah Chen",
      role: "CMO",
      company: "TechVenture",
      image: "https://images.unsplash.com/photo-1695048994291-2e96839a0a3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=100",
    },
    {
      id: 2,
      quote: "Finally, an agency that actually delivers. Strategy-led, creatively bold, and obsessed with results.",
      name: "Marcus Williams",
      role: "Founder",
      company: "GrowthRetail",
      image: "https://images.unsplash.com/photo-1758518732175-5d608ba3abdf?crop=entropy&cs=srgb&fm=jpg&q=85&w=100",
    },
  ];

  return (
    <section className="py-24 md:py-32" data-testid="testimonials-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="inline-block text-brand-accent uppercase tracking-widest text-sm font-semibold mb-4">
            Testimonials
          </span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white">
            What Clients Say
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={testimonial.id}
              className="testimonial-card p-8 animate-fade-in-up"
              style={{ animationDelay: `${index * 150}ms` }}
              data-testid={`testimonial-card-${index}`}
            >
              <Quote size={40} className="text-brand-primary/30 mb-6" />
              <p className="text-white text-lg mb-8 leading-relaxed">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center gap-4">
                <img 
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-14 h-14 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <p className="text-white font-semibold">{testimonial.name}</p>
                  <p className="text-white/60 text-sm">
                    {testimonial.role}, {testimonial.company}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    website: "",
    budget: "",
    message: "",
    newsletter: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Get UTM parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const utmData = {
      utm_source: urlParams.get("utm_source") || "",
      utm_medium: urlParams.get("utm_medium") || "",
      utm_campaign: urlParams.get("utm_campaign") || "",
      utm_content: urlParams.get("utm_content") || "",
      utm_term: urlParams.get("utm_term") || "",
    };

    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        ...utmData,
      });
      setIsSubmitted(true);
      toast.success("Thanks! We'll review your brief and reply within 48 hours.");
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const budgetOptions = [
    { value: "5k-10k", label: "$5K - $10K" },
    { value: "10k-25k", label: "$10K - $25K" },
    { value: "25k-50k", label: "$25K - $50K" },
    { value: "50k+", label: "$50K+" },
  ];

  if (isSubmitted) {
    return (
      <section id="contact" className="py-24 md:py-32 bg-white/[0.02]" data-testid="contact-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-brand-primary/20 border border-brand-primary flex items-center justify-center mx-auto mb-8">
              <Check size={40} className="text-brand-primary" />
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Message Received!
            </h2>
            <p className="text-white/70 text-lg" data-testid="thank-you-message">
              Thanks! We'll review your brief and reply within 48 hours.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-24 md:py-32 bg-white/[0.02]" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Content */}
          <div>
            <span className="inline-block text-brand-primary uppercase tracking-widest text-sm font-semibold mb-4">
              Let's Talk
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Ready to Start <br />
              <span className="text-brand-primary">Your Growth?</span>
            </h2>
            <p className="text-white/60 text-lg mb-8">
              Tell us about your project and we'll craft a custom strategy to help you scale.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Mail className="text-brand-primary" size={20} />
                <span className="text-white">hello@brandup.agency</span>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-white/70 text-sm mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="John Smith"
                  data-testid="form-name-input"
                  aria-label="Your name"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-white/70 text-sm mb-2">
                  Company *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Acme Inc."
                  data-testid="form-company-input"
                  aria-label="Your company name"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-white/70 text-sm mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="john@acme.com"
                  data-testid="form-email-input"
                  aria-label="Your email address"
                />
              </div>
              <div>
                <label htmlFor="website" className="block text-white/70 text-sm mb-2">
                  Website
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://acme.com"
                  data-testid="form-website-input"
                  aria-label="Your website URL"
                />
              </div>
            </div>

            <div>
              <label htmlFor="budget" className="block text-white/70 text-sm mb-2">
                Budget Bracket *
              </label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="form-budget-select"
                aria-label="Select your budget bracket"
              >
                <option value="">Select budget range</option>
                {budgetOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-white/70 text-sm mb-2">
                Brief Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={4}
                className="form-input resize-none"
                placeholder="Tell us about your project, goals, and timeline..."
                data-testid="form-message-textarea"
                aria-label="Your project brief"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="newsletter"
                name="newsletter"
                checked={formData.newsletter}
                onChange={handleChange}
                className="w-5 h-5 bg-transparent border border-white/30 text-brand-primary focus:ring-brand-primary"
                data-testid="form-newsletter-checkbox"
              />
              <label htmlFor="newsletter" className="text-white/60 text-sm">
                Subscribe to our newsletter for growth tips and insights
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="form-submit-btn"
            >
              {isSubmitting ? "Sending..." : "Start Your Growth"}
              {!isSubmitting && <ChevronRight size={18} />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const packages = [
    {
      name: "Starter",
      price: "$5K",
      description: "Perfect for startups and small businesses",
      features: [
        "Brand identity refresh",
        "Social media kit",
        "Landing page design",
        "2 weeks delivery",
      ],
      featured: false,
    },
    {
      name: "Growth",
      price: "$15K",
      description: "For scaling brands ready to level up",
      features: [
        "Full brand strategy",
        "Website design & dev",
        "Content production",
        "90-day marketing plan",
        "Monthly reporting",
      ],
      featured: true,
    },
    {
      name: "Scale",
      price: "$35K+",
      description: "Enterprise solutions for serious growth",
      features: [
        "Complete rebrand",
        "Multi-channel campaigns",
        "Video production suite",
        "Dedicated team",
        "Quarterly strategy reviews",
      ],
      featured: false,
    },
  ];

  return (
    <section id="pricing" className="py-24 md:py-32" data-testid="pricing-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="inline-block text-brand-secondary uppercase tracking-widest text-sm font-semibold mb-4">
            Packages
          </span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
            Transparent Pricing
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Flexible packages designed for businesses at every stage of growth.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <div 
              key={pkg.name}
              className={`pricing-card p-8 ${pkg.featured ? 'featured' : ''} animate-fade-in-up`}
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`pricing-card-${index}`}
            >
              {pkg.featured && (
                <span className="inline-block bg-brand-primary text-brand-bg text-xs font-bold uppercase tracking-wider px-3 py-1 mb-4">
                  Most Popular
                </span>
              )}
              <h3 className="font-heading font-bold text-2xl text-white mb-2">
                {pkg.name}
              </h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-heading font-black text-4xl text-brand-primary">
                  {pkg.price}
                </span>
                {pkg.name !== "Scale" && <span className="text-white/50">/project</span>}
              </div>
              <p className="text-white/60 mb-6">{pkg.description}</p>
              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <Check size={16} className="text-brand-primary flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={scrollToContact}
                className={pkg.featured ? 'btn-primary w-full' : 'btn-secondary w-full'}
                data-testid={`pricing-cta-${index}`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-16 border-t border-white/10" data-testid="footer">
      <div className="container-custom">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Logo & Mission */}
          <div className="md:col-span-2">
            <a href="/" className="flex items-center gap-2 mb-4" data-testid="footer-logo">
              <span className="font-heading font-bold text-2xl text-white">Brand</span>
              <span className="font-heading font-bold text-2xl text-brand-primary">Up+</span>
            </a>
            <p className="text-white/60 max-w-sm">
              Strategic brands. Creative content. Real results. We help ambitious brands scale.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading font-bold text-white mb-4">Navigation</h4>
            <ul className="space-y-2">
              <li><a href="#services" className="footer-link" data-testid="footer-link-services">Services</a></li>
              <li><a href="#work" className="footer-link" data-testid="footer-link-work">Work</a></li>
              <li><a href="#process" className="footer-link" data-testid="footer-link-process">Process</a></li>
              <li><a href="#pricing" className="footer-link" data-testid="footer-link-pricing">Pricing</a></li>
              <li><a href="#contact" className="footer-link" data-testid="footer-link-contact">Contact</a></li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-heading font-bold text-white mb-4">Connect</h4>
            <div className="flex gap-3">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn" data-testid="social-linkedin">
                <Linkedin size={18} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Twitter" data-testid="social-twitter">
                <Twitter size={18} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram" data-testid="social-instagram">
                <Instagram size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {currentYear} BrandUp+ Agency. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="/privacy" className="text-white/40 text-sm hover:text-white/60 transition-colors" data-testid="footer-privacy">
              Privacy Policy
            </a>
            <a href="/terms" className="text-white/40 text-sm hover:text-white/60 transition-colors" data-testid="footer-terms">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Thank You Page
const ThankYouPage = () => {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="thank-you-page">
      <div className="text-center max-w-lg px-6">
        <div className="w-20 h-20 bg-brand-primary/20 border border-brand-primary flex items-center justify-center mx-auto mb-8">
          <Check size={40} className="text-brand-primary" />
        </div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
          Thanks for Reaching Out!
        </h1>
        <p className="text-white/70 text-lg mb-8">
          We'll review your brief and reply within 48 hours.
        </p>
        <a href="/" className="btn-primary inline-flex items-center gap-2">
          Back to Home
          <ArrowRight size={18} />
        </a>
      </div>
    </div>
  );
};

// Landing Page
const LandingPage = () => {
  return (
    <>
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <WhySection />
        <ProcessSection />
        <CaseStudiesSection />
        <TestimonialsSection />
        <ContactSection />
        <PricingSection />
      </main>
      <Footer />
    </>
  );
};

// Main App
function App() {
  // Add SEO meta tags
  useEffect(() => {
    document.title = "BrandUp+ | Creative Agency for Startups & SMEs";
    
    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'BrandUp+ is a branding, digital marketing, and audiovisual production agency that grows visibility, credibility and sales for startups, SMEs and ambitious brands.';

    // Add Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'BrandUp+ | Turn Attention Into Growth' },
      { property: 'og:description', content: 'Strategic brands. Creative content. Real results. We help ambitious brands scale.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: '/og-image.png' },
    ];

    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.content = tag.content;
    });

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin;
  }, []);

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
